import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import classNames from "classnames";

import {
    getInviteApplications,
    getExpireApplications,
    getInterviewStateApplications,
    getRecentApplications,
    getIncompleteApplications,
    getAllApps,
} from "redux/candidate/selectors";
import { showModal } from "redux/ws/actions";
import { applicationShape, questionShape } from "lib/propTypes";
import { MOBILE_BREAK } from "lib/constants";
import { track } from "lib/analytics";
import { calculateMatchScore, getProgressClass } from "lib/util";
import Progress from "common/ProgressCircle";
import wsStorage from "lib/wsStorage";

import Interviews from "./Home/Interviews";
import RecentApps from "./Home/RecentApps";
import AlertCards from "./Home/AlertCards";
import ResumeApp from "./Home/ResumeApp";
import MatchScoreModal from "./MatchScoreModal";

const MATCH_SCORE_TOOLTIP_KEY = "mshome";

const mapStateToProps = (state) => ({
    invites: getInviteApplications(state),
    expired: getExpireApplications(state),
    incomplete: getIncompleteApplications(state),
    interviews: getInterviewStateApplications(state),
    recentApps: getRecentApplications(state),
    apps: getAllApps(state),
    complete: state.complete,
    firstName: state.firstName,
    gender: state.gender,
    hasJobRefs: state.hasJobRefs,
    hasPrefs: state.hasPrefs,
    matchScore: Math.round(calculateMatchScore(state.answerCount)),
    questions: (state.questions && state.questions.questions) || [],
});

class CandidateHome extends React.PureComponent {
    static propTypes = {
        apps: PropTypes.arrayOf(applicationShape).isRequired,
        complete: PropTypes.bool.isRequired,
        expired: PropTypes.arrayOf(applicationShape).isRequired,
        incomplete: PropTypes.arrayOf(applicationShape).isRequired,
        firstName: PropTypes.string.isRequired,
        gender: PropTypes.string.isRequired,
        hasJobAlerts: PropTypes.bool.isRequired,
        hasJobRefs: PropTypes.bool.isRequired,
        hasPrefs: PropTypes.bool.isRequired,
        interviews: PropTypes.arrayOf(applicationShape).isRequired,
        invites: PropTypes.arrayOf(applicationShape).isRequired,
        matchScore: PropTypes.number.isRequired,
        questions: PropTypes.arrayOf(questionShape),
        recentApps: PropTypes.arrayOf(applicationShape).isRequired,
        showModal: PropTypes.func.isRequired,
        subscriptionsURL: PropTypes.string.isRequired,
    };

    static defaultProps = {
        questions: [],
    };

    state = {
        seenMSTooltip: wsStorage.getItem(MATCH_SCORE_TOOLTIP_KEY),
    };

    onClickMatchScoreTooltip = (event) => {
        wsStorage.setItem(MATCH_SCORE_TOOLTIP_KEY, 1);
        this.setState({ seenMSTooltip: true });

        this.onShowMatchScoreModal(event);
    };

    onShowMatchScoreModal = (event) => {
        event.preventDefault();

        const { source } = event.target.dataset;
        track("MATCH_SCORE_MODAL", source);

        this.props.showModal(<MatchScoreModal matchScore={this.props.matchScore} />);
    };

    renderAlertArea() {
        if (!this.props.complete) {
            return <ResumeApp apps={this.props.apps} />;
        }

        if (
            this.props.invites.length === 0 &&
            this.props.expired.length === 0 &&
            this.props.hasPrefs &&
            this.props.questions.length === 0
        ) {
            return this.renderNoAlerts();
        }

        return (
            <AlertCards
                invites={this.props.invites}
                expired={this.props.expired}
                incomplete={this.props.incomplete}
                gender={this.props.gender}
                handleShowMatchScoreModal={this.onShowMatchScoreModal}
                hasJobAlerts={this.props.hasJobAlerts}
                hasJobRefs={this.props.hasJobRefs}
                hasPrefs={this.props.hasPrefs}
                questions={this.props.questions}
                subscriptionsURL={this.props.subscriptionsURL}
            />
        );
    }

    renderInterviews() {
        if (!this.props.complete) {
            return null;
        }
        return <Interviews interviews={this.props.interviews} />;
    }

    renderRecentApps() {
        if (!this.props.complete) {
            return null;
        }
        return <RecentApps apps={this.props.recentApps} />;
    }

    renderNoAlerts() {
        return (
            <div className="cand-home__top-no-alerts">
                <p>Check out more jobs that may be a good fit for you:</p>
                <a className="btn btn-primary btn-large" href="/jobs/">
                    Browse Jobs
                </a>
            </div>
        );
    }

    renderMatchScore() {
        if (!this.props.complete) {
            return null;
        }

        const { matchScore } = this.props;
        const progClass = getProgressClass(matchScore);
        const progWidth = window.innerWidth <= MOBILE_BREAK ? 32 : 56;
        let tooltip = null;

        if (!this.state.seenMSTooltip) {
            tooltip = (
                <div className="tooltip__container tooltip__container--top-left">
                    Introducing your <strong>Match Score</strong>
                    <br />
                    <a href="#" onClick={this.onClickMatchScoreTooltip} data-source="mstooltip">
                        Why is this important?
                    </a>
                </div>
            );
        }

        return (
            <div className={classNames("cand-home__match-score", progClass)}>
                <div className="cand-home__match-score-text">
                    <h3 className="progress-text">Match score</h3>
                    <a href="#" onClick={this.onShowMatchScoreModal} data-source="header">
                        What is this?
                    </a>
                </div>
                <Progress
                    className="cand-home__match-score-progress"
                    percentProgress={matchScore}
                    strokeWidth={3}
                    width={progWidth}
                >
                    <span className="progress-text tooltip__parent">
                        {tooltip}
                        {matchScore}
                        <em>%</em>
                    </span>
                </Progress>
            </div>
        );
    }

    render() {
        const { apps } = this.props;

        const metro = apps.length > 0 ? apps[0].position.metro : "";
        const classes = classNames("cand-home__top", {
            [`home-background-${metro.toLowerCase()}`]: !!metro,
        });

        return (
            <div>
                <div className={classes}>
                    <div className="cand-home__top-content">
                        <header>
                            <h1 className="cand-home__top-name fs-hide">
                                Hi {this.props.firstName}!
                            </h1>
                            {this.renderMatchScore()}
                        </header>
                        {this.renderAlertArea()}
                    </div>
                </div>
                {this.renderInterviews()}
                {this.renderRecentApps()}
            </div>
        );
    }
}

export default connect(mapStateToProps, { showModal })(CandidateHome);
